import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, ArrowLeft, Inbox } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  usePageMeta({ title: "Admin Inbox | Eli Capital Investments", noindex: true });

  const [token, setToken] = useState(() => sessionStorage.getItem("eci_admin_token") || "");
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [inquiries, setInquiries] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/inquiries`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setInquiries(data.inquiries))
      .catch(() => {
        sessionStorage.removeItem("eci_admin_token");
        setToken("");
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem("eci_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sign-in failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <main className="pt-[72px] pb-24" data-testid="admin-login-view">
        <div className="max-w-[480px] mx-auto px-5 mt-16">
          <a href="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-[14px] text-slate-500 hover:text-ink">
            <ArrowLeft size={16} /> Back to site
          </a>
          <form onSubmit={login} data-testid="admin-login-form" className="mt-8 border border-slate-200 rounded-2xl bg-surface p-8">
            <Lock size={28} className="text-accent" />
            <h1 className="mt-4 font-display font-bold text-2xl text-ink">Admin inbox</h1>
            <p className="mt-2 text-[14px] text-slate-500">
              Enter the owner passcode to view website inquiries.
            </p>
            <input
              type="password"
              required
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              data-testid="admin-passcode-input"
              className="mt-6 w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
              placeholder="Passcode"
              autoComplete="current-password"
            />
            {error && (
              <p role="alert" data-testid="admin-error-banner" className="mt-4 rounded-md border border-red-200 bg-red-50 text-red-700 text-[14px] px-4 py-3">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit-button"
              className="mt-6 w-full rounded-full bg-accent hover:bg-accentDeep disabled:opacity-60 text-white font-semibold px-6 py-3.5 text-[15px] transition-colors duration-200"
            >
              {busy ? "Checking…" : "Open inbox"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-[72px] pb-24" data-testid="admin-inbox-view">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 mt-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Inbox size={26} className="text-accent" />
            <h1 className="font-display font-bold text-3xl text-ink">Website inquiries</h1>
          </div>
          <a href="/" data-testid="admin-back-link-2" className="text-[14px] text-slate-500 hover:text-ink">
            Back to site
          </a>
        </div>

        {inquiries === null ? (
          <p className="mt-10 text-slate-500" data-testid="admin-loading">Loading…</p>
        ) : inquiries.length === 0 ? (
          <p className="mt-10 text-slate-500" data-testid="admin-empty-state">
            No inquiries yet. Submissions from the website form will appear here.
          </p>
        ) : (
          <div className="mt-10 overflow-x-auto border border-slate-200 rounded-2xl" data-testid="admin-inquiries-table">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="bg-surface text-left font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  <th className="px-4 py-3.5">Received</th>
                  <th className="px-4 py-3.5">Ref</th>
                  <th className="px-4 py-3.5">Name</th>
                  <th className="px-4 py-3.5">Contact</th>
                  <th className="px-4 py-3.5">Product</th>
                  <th className="px-4 py-3.5">Qty</th>
                  <th className="px-4 py-3.5">Destination</th>
                  <th className="px-4 py-3.5">Message</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.map((q) => (
                  <tr key={q.id} className="border-t border-slate-100 align-top" data-testid={`admin-inquiry-row-${q.reference}`}>
                    <td className="px-4 py-4 text-slate-500 whitespace-nowrap">{(q.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-4 font-mono text-[12px]">{q.reference}</td>
                    <td className="px-4 py-4 font-medium text-ink">{q.name}</td>
                    <td className="px-4 py-4 text-slate-600">
                      {q.email}
                      {q.phone ? <><br />{q.phone}</> : null}
                    </td>
                    <td className="px-4 py-4 text-slate-800 max-w-[220px]">{q.product}</td>
                    <td className="px-4 py-4 text-slate-800">{q.quantity}</td>
                    <td className="px-4 py-4 text-slate-800">{q.destination}</td>
                    <td className="px-4 py-4 text-slate-500 max-w-[240px]">{q.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
};

export default Admin;
